import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircle, AlertTriangle, Bot, CalendarDays, CircleDashed, Landmark, MessageSquareText, PhoneForwarded, PhoneOff, ShieldCheck, Smartphone } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { Button } from '../ui/Button';
import type { DashboardState, ForwardingAction, ForwardingConnection, RuleStatus } from '../../types/forwarding';
import { RULE_LABEL, STATUS_LABEL, configuredRules } from '../../utils/forwardingStatus';
export type CardAction = ForwardingAction | {
  type: 'manage';
};
type Tone = 'green' | 'gray' | 'amber' | 'red';
interface StateCopy {
  badge: string;
  tone: Tone;
  icon: React.ElementType;
  title: string;
  desc?: string;
  primary: {
    label: string;
    action: CardAction;
  };
  secondary: {
    label: string;
    action: CardAction;
  };
}
const COPY: Record<Exclude<DashboardState, 'firstTime'>, StateCopy> = {
  inactive: {
    badge: 'Inactive',
    tone: 'gray',
    icon: PhoneOff,
    title: 'Call Answering Is Off',
    desc: "Vexu isn't currently receiving forwarded calls from this number. Turn call answering back on whenever you're ready.",
    primary: {
      label: 'Turn On Call Answering',
      action: {
        type: 'turnOn'
      }
    },
    secondary: {
      label: 'Manage answering rules',
      action: {
        type: 'manage'
      }
    }
  },
  active: {
    badge: 'Active',
    tone: 'green',
    icon: ShieldCheck,
    title: 'Your AI Assistant Is Answering',
    primary: {
      label: 'Manage Answering Rules',
      action: {
        type: 'manage'
      }
    },
    secondary: {
      label: 'Turn off call answering',
      action: {
        type: 'turnOff'
      }
    }
  },
  partial: {
    badge: 'Partially Active',
    tone: 'amber',
    icon: AlertTriangle,
    title: 'Some Answering Rules Need Attention',
    desc: 'Vexu is set up for some situations, but other answering rules still need to be connected.',
    primary: {
      label: 'Complete Setup',
      action: {
        type: 'completeSetup'
      }
    },
    secondary: {
      label: 'Manage rules',
      action: {
        type: 'manage'
      }
    }
  },
  incomplete: {
    badge: 'Setup Incomplete',
    tone: 'amber',
    icon: CircleDashed,
    title: 'Finish Connecting Your Number',
    desc: "You're almost there. Complete your call-forwarding setup so Vexu can start answering.",
    primary: {
      label: 'Continue Setup',
      action: {
        type: 'continueSetup'
      }
    },
    secondary: {
      label: 'Change number',
      action: {
        type: 'changeNumber'
      }
    }
  },
  attention: {
    badge: 'Needs Attention',
    tone: 'red',
    icon: AlertCircle,
    title: 'Check Your Call Forwarding',
    desc: "We couldn't confirm that your calls are reaching Vexu. Let's check your setup.",
    primary: {
      label: 'Check Connection',
      action: {
        type: 'checkConnection'
      }
    },
    secondary: {
      label: 'Review answering rules',
      action: {
        type: 'manage'
      }
    }
  }
};
const FEATURES = [{
  icon: PhoneForwarded,
  label: 'AI call handling'
}, {
  icon: CalendarDays,
  label: 'Smart scheduling'
}, {
  icon: MessageSquareText,
  label: 'Personalized greetings'
}];
interface AnsweringStatusCardProps {
  state: DashboardState;
  connection: ForwardingConnection | null;
  subscriptionActive: boolean;
  onAction: (action: CardAction) => void;
}

/** The Home "AI Call Answering" card — one component that adapts to the account's real forwarding state. */
export function AnsweringStatusCard({
  state,
  connection,
  subscriptionActive,
  onAction
}: AnsweringStatusCardProps) {
  const {
    isLight
  } = useTheme();
  return <section className="space-y-3" data-tour="call-forwarding" aria-labelledby="ai-answering-heading" data-id="element-531" data-mp="xh67t9rzg1kz">
      <h2 id="ai-answering-heading" className={`text-sm font-bold uppercase tracking-wider ${isLight ? 'text-slate-500' : 'text-zinc-500'}`} data-id="element-532" data-mp="12c29z2qxo5bt">
        AI Call Answering
      </h2>
      <AnimatePresence mode="wait" initial={false} data-id="element-533" data-mp="shtmwuko94l1">
        <motion.div key={state} initial={{
        opacity: 0,
        y: 4
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -4
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className={`rounded-3xl border p-5 ${isLight ? 'border-slate-200/70 bg-white shadow-sm' : 'border-white/10 bg-[#111111]'}`} data-id="element-534" data-mp="elg6lorvj985">
          {state === 'firstTime' || !connection ? <FirstTimeContent onSetUp={() => onAction({
          type: 'setUp'
        })} trialEligible={!subscriptionActive} isLight={isLight} data-id="element-535" data-mp="1r5yyh3l95rb" /> : <StatusContent copy={COPY[state as Exclude<DashboardState, 'firstTime'>]} state={state} connection={connection} subscriptionActive={subscriptionActive} onAction={onAction} isLight={isLight} data-id="element-536" data-mp="3oyzw02kh5c5g" />}
        </motion.div>
      </AnimatePresence>
    </section>;
}
function FirstTimeContent({
  onSetUp,
  trialEligible,
  isLight
}: {
  onSetUp: () => void;
  trialEligible: boolean;
  isLight: boolean;
}) {
  return <>
      <div className="mb-4 flex items-start justify-between gap-3" data-id="element-537" data-mp="1gqbgi79d313d">
        <div className={`flex h-12 w-12 items-center justify-center rounded-2xl ${isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/15 text-vexu'}`} data-id="element-538" data-mp="1gnz1a1uzn4u0">
          <Bot className="h-6 w-6" data-id="element-539" data-mp="1pjojk2153j36" />
        </div>
        {trialEligible && <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/15 text-vexu'}`} data-id="element-540" data-mp="1uhq30o2h9kf6">
            3-Day Free Trial
          </span>}
      </div>
      <h3 className={`text-lg font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-541" data-mp="11tay4ud28ihk">Meet Your AI Assistant</h3>
      <p className={`mt-1.5 text-sm leading-relaxed ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-542" data-mp="16typ5g1agzbs">
        Let Vexu answer your calls, take messages, and help schedule meetings. Connect your number to get started.
      </p>
      <Button fullWidth className="mt-5" onClick={onSetUp} data-tour="activate-button" data-id="element-543" data-mp="2n5vnd9pmd7eh">
        Get Started
      </Button>
      <ul className={`mt-5 space-y-2.5 border-t pt-4 ${isLight ? 'border-slate-100' : 'border-white/5'}`} data-id="element-544" data-mp="1xh6znn7mugzv">
        {FEATURES.map(f => <li key={f.label} className="flex items-center gap-2.5" data-id="element-545" data-mp="1sinf9r8otd9m">
            <f.icon className={`h-4 w-4 flex-shrink-0 ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-546" data-mp="27f4oocxbghtj" />
            <span className={`text-[13px] font-medium ${isLight ? 'text-slate-600' : 'text-zinc-300'}`} data-id="element-547" data-mp="22i4ebptfoswm">{f.label}</span>
          </li>)}
      </ul>
    </>;
}
function StatusContent({
  copy,
  state,
  connection,
  subscriptionActive,
  onAction,
  isLight
}: {
  copy: StateCopy;
  state: DashboardState;
  connection: ForwardingConnection;
  subscriptionActive: boolean;
  onAction: (a: CardAction) => void;
  isLight: boolean;
}) {
  const rules = configuredRules(connection);
  const allMode = connection.mode === 'all';
  const modeLine = state === 'inactive' ? allMode ? 'Was answering every call' : "Was answering when you couldn't" : allMode ? 'Answering every incoming call' : "Answering when you can't";
  const NumberIcon = connection.numberType === 'landline' ? Landmark : Smartphone;
  const Icon = copy.icon;
  return <>
      <div className="mb-4 flex items-start justify-between gap-3" data-id="element-548" data-mp="2ss4woji0mfzj">
        <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${toneTile(copy.tone, isLight)}`} data-id="element-549" data-mp="2xpwzw9o41oj1">
          <Icon className="h-5 w-5" data-id="element-550" data-mp="3lf92ursuh0cw" />
        </div>
        <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${toneBadge(copy.tone, isLight)}`} data-id="element-551" data-mp="3ldob19xwzviq">
          {copy.badge}
        </span>
      </div>

      <h3 className={`text-lg font-bold leading-snug ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-552" data-mp="3ce182jpjehnk">{copy.title}</h3>
      {copy.desc && <p className={`mt-1.5 text-sm leading-relaxed ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-553" data-mp="3gir5iczug4al">{copy.desc}</p>}

      {/* Connected number + when Vexu answers */}
      <div className={`mt-4 flex items-center gap-3 rounded-2xl px-3.5 py-3 ${isLight ? 'bg-slate-50' : 'bg-white/[0.04]'}`} data-id="element-554" data-mp="2532gzh6xyx7o">
        <NumberIcon className={`h-4 w-4 flex-shrink-0 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-555" data-mp="2wmzl9xgjldm6" />
        <div className="min-w-0" data-id="element-556" data-mp="2rsc30gc4tfg1">
          <p className={`truncate text-sm font-bold tabular-nums ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-557" data-mp="2nnm7wtpas4w6">
            {connection.phoneNumber}
          </p>
          <p className={`truncate text-xs ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-558" data-mp="2irnpdmqnhh3o">
            {connection.numberType === 'landline' ? 'Landline' : 'Mobile'} · {modeLine}
          </p>
        </div>
      </div>

      {/* Rule summary */}
      {!allMode && rules.length > 0 && state !== 'inactive' && (state === 'partial' ? <ul className={`mt-3 divide-y rounded-2xl border ${isLight ? 'divide-slate-100 border-slate-100' : 'divide-white/5 border-white/5'}`} data-id="element-559" data-mp="31n43brx3mgei">
            {rules.map(({
        rule,
        status
      }) => <li key={rule} className="flex items-center justify-between px-3.5 py-2.5" data-id="element-560" data-mp="3ubt4ts22gnqs">
                <span className="flex items-center gap-2.5" data-id="element-561" data-mp="5jee1pvnzdt3">
                  <span className={`h-2 w-2 rounded-full ${dot(status)}`} aria-hidden data-id="element-562" data-mp="5kxtxfdermuc" />
                  <span className={`text-[13px] font-medium ${isLight ? 'text-slate-700' : 'text-zinc-200'}`} data-id="element-563" data-mp="ajsccg7gr9dx">
                    {RULE_LABEL[rule]}
                  </span>
                </span>
                <span className={`text-xs font-semibold ${statusText(status, isLight)}`} data-id="element-564" data-mp="jg9onmrkddb9">{STATUS_LABEL[status]}</span>
              </li>)}
          </ul> : <div className="mt-3 flex flex-wrap gap-1.5" data-id="element-565" data-mp="tez5rdbmytqg">
            {rules.filter(r => state !== 'active' || r.status === 'verified').map(({
        rule,
        status
      }) => <span key={rule} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${isLight ? 'border-slate-200 text-slate-600' : 'border-white/10 text-zinc-300'}`} title={STATUS_LABEL[status]} data-id="element-566" data-mp="135tucoccu5f2">
                  <span className={`h-1.5 w-1.5 rounded-full ${dot(status)}`} aria-hidden data-id="element-567" data-mp="11aci75nsi3b0" />
                  {RULE_LABEL[rule]}
                  <span className="sr-only" data-id="element-568" data-mp="1b6pb41bigd4p">: {STATUS_LABEL[status]}</span>
                </span>)}
          </div>)}

      <Button fullWidth className="mt-5" onClick={() => onAction(copy.primary.action)} data-id="element-569" data-mp="1fy7fz0gnmxuy">
        {copy.primary.label}
      </Button>
      <div className="mt-2 flex items-center justify-between gap-3" data-id="element-570" data-mp="1k13pm1o7vjmr">
        <button type="button" onClick={() => onAction(copy.secondary.action)} className={`py-2 text-[13px] font-semibold transition-colors ${isLight ? 'text-slate-500 hover:text-slate-800' : 'text-zinc-400 hover:text-white'}`} data-id="element-571" data-mp="1owsribnplcm8">
          {copy.secondary.label}
        </button>
        {state !== 'firstTime' && state !== 'incomplete' && <button type="button" onClick={() => onAction({
        type: 'customize'
      })} className={`py-2 text-[13px] font-semibold transition-colors ${isLight ? 'text-vexu-700 hover:text-vexu-600' : 'text-vexu hover:text-vexu-400'}`} data-id="element-572" data-mp="4hh6n8gxppx4">
            Customize assistant
          </button>}
      </div>
    </>;
}
export const dot = (s: RuleStatus) => s === 'verified' ? 'bg-vexu' : s === 'failed' ? 'bg-red-500' : s === 'off' ? 'bg-slate-300' : 'bg-amber-400';
export const statusText = (s: RuleStatus, isLight: boolean) => s === 'verified' ? isLight ? 'text-vexu-700' : 'text-vexu' : s === 'failed' ? isLight ? 'text-red-600' : 'text-red-400' : s === 'off' ? isLight ? 'text-slate-400' : 'text-zinc-500' : isLight ? 'text-amber-700' : 'text-amber-300';
const toneBadge = (t: Tone, isLight: boolean) => ({
  green: isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/15 text-vexu',
  gray: isLight ? 'bg-slate-100 text-slate-500' : 'bg-white/10 text-zinc-400',
  amber: isLight ? 'bg-amber-50 text-amber-700' : 'bg-amber-500/15 text-amber-300',
  red: isLight ? 'bg-red-50 text-red-600' : 'bg-red-500/15 text-red-400'
})[t];
const toneTile = (t: Tone, isLight: boolean) => ({
  green: isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/15 text-vexu',
  gray: isLight ? 'bg-slate-100 text-slate-500' : 'bg-white/10 text-zinc-400',
  amber: isLight ? 'bg-amber-50 text-amber-600' : 'bg-amber-500/15 text-amber-300',
  red: isLight ? 'bg-red-50 text-red-500' : 'bg-red-500/15 text-red-400'
})[t];